import { Link } from "react-router-dom";
import { Instagram, Mail, MapPin, Heart } from "lucide-react";

const ZelligePattern = () => (
  <svg className="absolute inset-0 w-full h-full opacity-[0.03] pointer-events-none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <pattern id="zellige-footer" x="0" y="0" width="60" height="60" patternUnits="userSpaceOnUse">
        <path d="M30 0L60 30L30 60L0 30Z" fill="none" stroke="currentColor" strokeWidth="0.5" />
        <circle cx="30" cy="30" r="4" fill="none" stroke="currentColor" strokeWidth="0.3" />
      </pattern>
    </defs>
    <rect width="100%" height="100%" fill="url(#zellige-footer)" />
  </svg>
);

export const Footer = () => {
  return (
    <footer className="relative overflow-hidden">
      {/* Top gradient accent bar */}
      <div className="h-1 bg-gradient-to-r from-[hsl(var(--terracotta))] via-[hsl(var(--gold))] to-[hsl(var(--terracotta))]" />

      {/* Main footer */}
      <div className="relative bg-gradient-to-b from-[hsl(var(--foreground))] to-[hsl(var(--foreground)/0.95)]">
        <ZelligePattern />
        {/* Radial glows */}
        <div className="absolute top-0 left-1/4 w-80 h-80 bg-[hsl(var(--terracotta)/0.08)] rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-72 h-72 bg-[hsl(var(--gold)/0.06)] rounded-full blur-3xl" />

        <div className="container mx-auto px-4 py-14 relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-10">
            {/* Brand */}
            <div className="md:col-span-1">
              <h3
                className="font-display font-bold text-2xl mb-3"
                style={{
                  backgroundImage: "linear-gradient(135deg, hsl(18 78% 55%), hsl(42 72% 60%))",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                EL FAZA
              </h3>
              <p className="text-sm text-white/50 font-body leading-relaxed mb-4">
                Adhésifs nés de la nostalgie d'un pays et de la reconnaissance envers un autre.
              </p>
              <div className="flex items-center gap-1.5 text-white/30">
                <span className="text-xs font-body">De la Tunisie au Canada</span>
                <span>🇹🇳</span>
                <Heart className="w-3 h-3 text-[hsl(var(--terracotta))]" />
                <span>🇨🇦</span>
              </div>
            </div>

            {/* Navigation */}
            <div>
              <h4 className="font-display font-semibold text-xs uppercase tracking-[0.2em] mb-5 text-[hsl(var(--gold))]">
                Navigation
              </h4>
              <div className="flex flex-col gap-3">
                {[
                  { label: "Accueil", path: "/" },
                  { label: "Notre Histoire", path: "/notre-histoire" },
                  { label: "Contact", path: "/contact" },
                ].map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    className="text-sm text-white/50 font-body hover:text-[hsl(var(--terracotta-glow))] transition-colors duration-300"
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            </div>

            {/* Contact */}
            <div>
              <h4 className="font-display font-semibold text-xs uppercase tracking-[0.2em] mb-5 text-[hsl(var(--gold))]">
                Contact
              </h4>
              <div className="space-y-3">
                <a
                  href="mailto:elfaza.contact@gmail.com"
                  className="flex items-center gap-2 text-sm text-white/50 font-body hover:text-[hsl(var(--terracotta-glow))] transition-colors duration-300"
                >
                  <Mail className="w-4 h-4 flex-shrink-0" />
                  elfaza.contact@gmail.com
                </a>
                <div className="flex items-center gap-2 text-sm text-white/40 font-body">
                  <MapPin className="w-4 h-4 flex-shrink-0" />
                  Canada
                </div>
              </div>
            </div>

            {/* Social */}
            <div>
              <h4 className="font-display font-semibold text-xs uppercase tracking-[0.2em] mb-5 text-[hsl(var(--gold))]">
                Suivez-nous
              </h4>
              <div className="flex items-center gap-3">
                <a
                  href="https://www.instagram.com/elfaza_vibes"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Instagram EL FAZA"
                  className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-white/60 hover:bg-gradient-to-br hover:from-[hsl(var(--terracotta))] hover:to-[hsl(var(--terracotta-glow))] hover:text-white hover:border-transparent hover:shadow-[var(--shadow-warm)] transition-all duration-300"
                >
                  <Instagram className="w-4 h-4" />
                </a>
                <a
                  href="https://www.tiktok.com/@elfaza_vibes"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="TikTok EL FAZA"
                  className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-white/60 hover:bg-white hover:text-foreground hover:border-transparent hover:shadow-lg transition-all duration-300"
                >
                  <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1v-3.51a6.37 6.37 0 0 0-.79-.05A6.34 6.34 0 0 0 3.15 15a6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.34-6.34V9.05a8.26 8.26 0 0 0 4.76 1.51V7.12a4.82 4.82 0 0 1-1-.43z" />
                  </svg>
                </a>
              </div>
            </div>
          </div>

          {/* Bottom bar */}
          <div className="border-t border-white/10 mt-12 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              {[1, 2, 3, 4, 5].map((i) => (
                <div
                  key={i}
                  className="w-1.5 h-1.5 rounded-full"
                  style={{
                    background: i % 2 === 0
                      ? "hsl(42 72% 55%)"
                      : "hsl(18 78% 50%)",
                    opacity: 0.4,
                  }}
                />
              ))}
            </div>
            <p className="text-xs text-white/30 font-body">
              © {new Date().getFullYear()} EL FAZA Canada. Tous droits réservés.
              <span className="mx-1">·</span>
              <Link to="/admin/login" className="text-white/20 hover:text-white/40 transition-colors">
                Admin
              </Link>
            </p>
            <div className="flex items-center gap-2">
              {[1, 2, 3, 4, 5].map((i) => (
                <div
                  key={i}
                  className="w-1.5 h-1.5 rounded-full"
                  style={{
                    background: i % 2 === 0
                      ? "hsl(42 72% 55%)"
                      : "hsl(18 78% 50%)",
                    opacity: 0.4,
                  }}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
